import { useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement,
  Title, Tooltip, Legend,
} from 'chart.js';
import { splitSpecies } from '../../../utils/species';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface Props { allData: any[] }

/* ── Sentencing parser ─────────────────────────────────────────────── */

type Outcome = 'imprisoned' | 'fined' | 'both' | 'acquitted' | 'unknown';

interface Parsed {
  type:         Outcome;
  fineKES:      number | null;   // fine amount in KES
  imprisonMon:  number | null;   // imprisonment in months
}

function parseSentencing(sentencing: string | null, judgmentOutcome: string | null): Parsed {
  const raw = [sentencing, judgmentOutcome].filter(Boolean).join(' ');
  if (!raw.trim()) return { type: 'unknown', fineKES: null, imprisonMon: null };

  const lc = raw.toLowerCase();

  // Acquitted / dismissed
  if (/acquit|dismiss|not\s+guilty|discharged|withdrawn|no\s+case/i.test(lc)) {
    return { type: 'acquitted', fineKES: null, imprisonMon: null };
  }

  // Fine amount — match patterns like "KES 50,000" / "Ksh. 200,000" / "fine of 10000" / "100,000/="
  const finePatterns = [
    /(?:kes|ksh\.?|sh\.?)\s*([0-9][0-9,]*)/i,
    /fine\s+of\s+(?:kes\s*|ksh\s*)?([0-9][0-9,]*)/i,
    /([0-9][0-9,]+)\s*\/=/,
    /([0-9][0-9,]+)\s*(?:kes|ksh|shillings?)/i,
  ];
  let fineKES: number | null = null;
  for (const pat of finePatterns) {
    const m = raw.match(pat);
    if (m) { fineKES = parseInt(m[1].replace(/,/g, '')); break; }
  }

  // Imprisonment — years and/or months
  const yearsM  = raw.match(/(\d+)\s*(?:years?|yrs?)/i);
  const monthsM = raw.match(/(\d+)\s*months?/i);
  const years   = yearsM  ? parseInt(yearsM[1])  : 0;
  const months  = monthsM ? parseInt(monthsM[1]) : 0;
  const imprisonMon = (years * 12 + months) || null;

  if (!fineKES && !imprisonMon) return { type: 'unknown', fineKES: null, imprisonMon: null };

  const type: Outcome =
    fineKES && imprisonMon ? 'both' :
    fineKES                ? 'fined' :
    'imprisoned';

  return { type, fineKES, imprisonMon };
}

/* ── Chart options ─────────────────────────────────────────────────── */
const baseOpts = {
  responsive:          true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    title:  { display: false },
  },
  scales: {
    x: { ticks: { color: '#6b7280', font: { size: 9 } }, grid: { color: 'rgba(255,255,255,0.04)' } },
    y: { ticks: { color: '#6b7280', font: { size: 9 } }, grid: { color: 'rgba(255,255,255,0.06)' } },
  },
};

/* ── Helpers ───────────────────────────────────────────────────────── */
const OUTCOME_CLS: Record<Outcome, string> = {
  imprisoned: 'bg-red-500/15 text-red-400 border-red-500/25',
  fined:      'bg-amber-500/15 text-amber-400 border-amber-500/25',
  both:       'bg-violet-500/15 text-violet-400 border-violet-500/25',
  acquitted:  'bg-emerald-500/15 text-emerald-400 border-emerald-500/25',
  unknown:    'bg-white/8 text-white/35 border-white/12',
};

const OUTCOME_LABEL: Record<Outcome, string> = {
  imprisoned: 'Imprisoned',
  fined:      'Fined Only',
  both:       'Fine + Prison',
  acquitted:  'Acquitted',
  unknown:    'Unreported',
};

function fmtKES(v: number): string {
  if (v >= 1_000_000) return `KES ${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `KES ${(v / 1_000).toFixed(0)}K`;
  return `KES ${v}`;
}

function fmtMonths(m: number): string {
  if (m >= 12) return `${(m / 12).toFixed(1).replace('.0', '')} yr${m >= 24 ? 's' : ''}`;
  return `${m} mo`;
}

/* ── Main component ─────────────────────────────────────────────────── */

export default function SentencingIntelligenceView({ allData }: Props) {
  const [tab, setTab] = useState<'overview' | 'species' | 'courts' | 'trends'>('overview');

  /* ── Parse all cases ── */
  const parsed = useMemo(() =>
    allData.map((d: any) => ({
      ...d,
      _s: parseSentencing(d.sentencing, d.judgmentOutcome),
    })),
  [allData]);

  const withData = parsed.filter((d) => d._s.type !== 'unknown');

  /* ── Outcome distribution ── */
  const outcomeCounts = useMemo(() => {
    const counts: Record<Outcome, number> = { imprisoned: 0, fined: 0, both: 0, acquitted: 0, unknown: 0 };
    parsed.forEach((d) => counts[d._s.type as Outcome]++);
    return counts;
  }, [parsed]);

  /* ── Fine stats ── */
  const fineStats = useMemo(() => {
    const fines = parsed.filter((d) => d._s.fineKES).map((d) => d._s.fineKES as number).sort((a, b) => a - b);
    if (!fines.length) return null;
    return {
      min:    fines[0],
      max:    fines[fines.length - 1],
      median: fines[Math.floor(fines.length / 2)],
      avg:    Math.round(fines.reduce((s, v) => s + v, 0) / fines.length),
      count:  fines.length,
    };
  }, [parsed]);

  /* ── Imprisonment stats ── */
  const imprisonStats = useMemo(() => {
    const vals = parsed.filter((d) => d._s.imprisonMon).map((d) => d._s.imprisonMon as number).sort((a, b) => a - b);
    if (!vals.length) return null;
    return {
      min:    vals[0],
      max:    vals[vals.length - 1],
      median: vals[Math.floor(vals.length / 2)],
      avg:    Math.round(vals.reduce((s, v) => s + v, 0) / vals.length),
      count:  vals.length,
    };
  }, [parsed]);

  /* ── By species ── */
  const bySpecies = useMemo(() => {
    const map: Record<string, { total: number; acquitted: number; imprisoned: number; avgFine: number[]; avgPrison: number[] }> = {};
    parsed.forEach((d) => {
      // A case can list several species (e.g. "Rhino; Pangolin; Elephant") —
      // credit its sentencing outcome to each one rather than only the first.
      const species = splitSpecies(d.species).map((s) => s.toLowerCase());
      (species.length ? species : ['unknown']).forEach((sp) => {
        if (!map[sp]) map[sp] = { total: 0, acquitted: 0, imprisoned: 0, avgFine: [], avgPrison: [] };
        map[sp].total++;
        if (d._s.type === 'acquitted') map[sp].acquitted++;
        if (d._s.imprisonMon) { map[sp].imprisoned++; map[sp].avgPrison.push(d._s.imprisonMon); }
        if (d._s.fineKES) map[sp].avgFine.push(d._s.fineKES);
      });
    });
    return Object.entries(map)
      .filter(([, v]) => v.total >= 2)
      .map(([sp, v]) => ({
        sp,
        total:        v.total,
        acquitRate:   Math.round((v.acquitted / v.total) * 100),
        imprisonRate: Math.round((v.imprisoned / v.total) * 100),
        avgFine:      v.avgFine.length ? Math.round(v.avgFine.reduce((a, b) => a + b) / v.avgFine.length) : null,
        avgPrison:    v.avgPrison.length ? Math.round(v.avgPrison.reduce((a, b) => a + b) / v.avgPrison.length) : null,
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
  }, [parsed]);

  /* ── By court ── */
  const byCourt = useMemo(() => {
    const map: Record<string, { total: number; concluded: number; acquitted: number }> = {};
    allData.forEach((d: any) => {
      const court = d.court?.trim();
      if (!court) return;
      if (!map[court]) map[court] = { total: 0, concluded: 0, acquitted: 0 };
      map[court].total++;
      const s = d.caseStatus?.toLowerCase();
      if (s === 'concluded') map[court].concluded++;
      const { type } = parseSentencing(d.sentencing, d.judgmentOutcome);
      if (type === 'acquitted') map[court].acquitted++;
    });
    return Object.entries(map)
      .filter(([, v]) => v.total >= 2)
      .map(([court, v]) => ({
        court,
        total:       v.total,
        convictRate: Math.round((v.concluded / v.total) * 100),
        acquitRate:  Math.round((v.acquitted / v.total) * 100),
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 8);
  }, [allData]);

  /* ── Year trends ── */
  const yearTrends = useMemo(() => {
    const map: Record<string, { acquitted: number; imprisoned: number; fined: number; both: number }> = {};
    parsed.forEach((d) => {
      if (!d.dateOfSeizure) return;
      const y = new Date(d.dateOfSeizure).getFullYear();
      if (y < 2005 || y > new Date().getFullYear()) return;
      const key = String(y);
      if (!map[key]) map[key] = { acquitted: 0, imprisoned: 0, fined: 0, both: 0 };
      const t = d._s.type as Outcome;
      if (t !== 'unknown') map[key][t]++;
    });
    const labels = Object.keys(map).sort();
    return {
      labels,
      acquitted:  labels.map((y) => map[y].acquitted),
      imprisoned: labels.map((y) => map[y].imprisoned),
      fined:      labels.map((y) => map[y].fined),
      both:       labels.map((y) => map[y].both),
    };
  }, [parsed]);

  const TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'species',  label: 'By Species' },
    { id: 'courts',   label: 'By Court' },
    { id: 'trends',   label: 'Trends' },
  ] as const;

  return (
    <div className="flex-1 overflow-y-auto p-5" style={{ scrollbarWidth: 'thin', scrollbarColor: '#14532d transparent' }}>

      {/* Header */}
      <div className="mb-5">
        <h2 className="text-white/85 text-base font-semibold" style={{ fontFamily: 'var(--font-display)' }}>Sentencing Intelligence</h2>
        <p className="text-white/30 text-xs mt-0.5">Judicial outcome analysis — fines, imprisonment, acquittals across species and courts</p>
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-1 mb-5 bg-white/4 rounded-xl p-1 w-fit">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              tab === t.id
                ? 'bg-green-700/70 text-green-300 border border-green-500/25'
                : 'text-white/35 hover:text-white/60'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Overview tab ── */}
      {tab === 'overview' && (
        <>
          {/* Outcome breakdown */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
            {(Object.keys(OUTCOME_LABEL) as Outcome[]).map((o) => (
              <div key={o} className="rounded-xl border border-white/6 px-4 py-3" style={{ background: '#0a2e18' }}>
                <span className="text-white/30 text-[10px] uppercase tracking-wider block">{OUTCOME_LABEL[o]}</span>
                <p className="text-2xl font-bold tabular-nums mt-1 text-white/85" style={{ fontFamily: 'var(--font-display)' }}>
                  {outcomeCounts[o]}
                </p>
                <span className={`mt-1.5 inline-flex px-1.5 py-0.5 rounded-full border text-[8px] font-semibold ${OUTCOME_CLS[o]}`}>
                  {allData.length > 0 ? Math.round((outcomeCounts[o] / allData.length) * 100) : 0}%
                </span>
              </div>
            ))}
          </div>

          {/* Fine + imprisonment stat cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">

            {/* Fine stats */}
            <div className="rounded-xl border border-amber-500/15 p-5" style={{ background: '#0a2e18' }}>
              <p className="text-amber-400/70 text-xs font-semibold tracking-[0.1em] uppercase mb-4">Fine Statistics</p>
              {fineStats ? (
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'Median Fine',  value: fmtKES(fineStats.median) },
                    { label: 'Average Fine', value: fmtKES(fineStats.avg) },
                    { label: 'Minimum',      value: fmtKES(fineStats.min) },
                    { label: 'Maximum',      value: fmtKES(fineStats.max) },
                  ].map((s) => (
                    <div key={s.label}>
                      <p className="text-white/25 text-[9px] uppercase tracking-wider">{s.label}</p>
                      <p className="text-amber-400 text-sm font-bold mt-0.5">{s.value}</p>
                    </div>
                  ))}
                  <div className="col-span-2 pt-2 border-t border-white/5">
                    <p className="text-white/25 text-[9px]">{fineStats.count} cases with recorded fines</p>
                  </div>
                </div>
              ) : (
                <p className="text-white/20 text-xs italic">No fine data parseable from current dataset</p>
              )}
            </div>

            {/* Imprisonment stats */}
            <div className="rounded-xl border border-red-500/15 p-5" style={{ background: '#0a2e18' }}>
              <p className="text-red-400/70 text-xs font-semibold tracking-[0.1em] uppercase mb-4">Imprisonment Statistics</p>
              {imprisonStats ? (
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'Median Term',    value: fmtMonths(imprisonStats.median) },
                    { label: 'Average Term',   value: fmtMonths(imprisonStats.avg) },
                    { label: 'Shortest',       value: fmtMonths(imprisonStats.min) },
                    { label: 'Longest',        value: fmtMonths(imprisonStats.max) },
                  ].map((s) => (
                    <div key={s.label}>
                      <p className="text-white/25 text-[9px] uppercase tracking-wider">{s.label}</p>
                      <p className="text-red-400 text-sm font-bold mt-0.5">{s.value}</p>
                    </div>
                  ))}
                  <div className="col-span-2 pt-2 border-t border-white/5">
                    <p className="text-white/25 text-[9px]">{imprisonStats.count} cases with recorded imprisonment</p>
                  </div>
                </div>
              ) : (
                <p className="text-white/20 text-xs italic">No imprisonment data parseable from current dataset</p>
              )}
            </div>
          </div>

          {/* Outcome distribution chart */}
          <div className="rounded-xl border border-white/6 p-5" style={{ background: '#0a2e18' }}>
            <p className="text-white/55 text-xs font-semibold tracking-[0.1em] uppercase mb-3">Outcome Distribution</p>
            <div style={{ height: 200 }}>
              <Bar
                options={baseOpts as any}
                data={{
                  labels: Object.values(OUTCOME_LABEL),
                  datasets: [{
                    label: 'Cases',
                    data: (Object.keys(OUTCOME_LABEL) as Outcome[]).map((o) => outcomeCounts[o]),
                    backgroundColor: ['rgba(239,68,68,0.7)', 'rgba(251,191,36,0.7)', 'rgba(167,139,250,0.7)', 'rgba(52,211,153,0.7)', 'rgba(156,163,175,0.4)'],
                    borderRadius: 4,
                  }],
                }}
              />
            </div>
          </div>

          {/* No parsed data note */}
          {withData.length === 0 && (
            <div className="mt-4 rounded-xl border border-amber-500/15 p-4 text-center" style={{ background: 'rgba(251,191,36,0.04)' }}>
              <p className="text-amber-400/70 text-xs">
                Sentencing data is present in {allData.filter((d: any) => d.sentencing || d.judgmentOutcome).length} records but could not be parsed into structured format.
                Fine and imprisonment statistics will appear once data is entered in a parseable format (e.g. &quot;KES 50,000&quot; or &quot;3 years imprisonment&quot;).
              </p>
            </div>
          )}
        </>
      )}

      {/* ── By Species tab ── */}
      {tab === 'species' && (
        <div className="flex flex-col gap-4">
          <div className="rounded-xl border border-white/6 p-5" style={{ background: '#0a2e18' }}>
            <p className="text-white/55 text-xs font-semibold tracking-[0.1em] uppercase mb-4">Acquittal Rate by Species</p>
            {bySpecies.length > 0 ? (
              <div style={{ height: 240 }}>
                <Bar
                  options={{
                    ...baseOpts,
                    scales: {
                      ...baseOpts.scales,
                      y: { ...baseOpts.scales.y, max: 100, ticks: { ...baseOpts.scales.y.ticks, callback: (v: any) => `${v}%` } },
                    },
                    plugins: { ...baseOpts.plugins, legend: { display: true, labels: { color: '#9ca3af', font: { size: 9 }, boxWidth: 8 } } },
                  } as any}
                  data={{
                    labels: bySpecies.map((s) => s.sp),
                    datasets: [
                      { label: 'Acquitted %',  data: bySpecies.map((s) => s.acquitRate),   backgroundColor: 'rgba(52,211,153,0.7)',  borderRadius: 3 },
                      { label: 'Imprisoned %', data: bySpecies.map((s) => s.imprisonRate), backgroundColor: 'rgba(239,68,68,0.7)',   borderRadius: 3 },
                    ],
                  }}
                />
              </div>
            ) : (
              <p className="text-white/20 text-sm text-center py-8">Insufficient data for species analysis</p>
            )}
          </div>

          {/* Species table */}
          <div className="rounded-xl border border-white/6 overflow-hidden" style={{ background: '#0a2e18' }}>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-white/5">
                  {['Species', 'Cases', 'Acquit %', 'Prison %', 'Avg Fine', 'Avg Term'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-white/30 font-semibold text-[10px] uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-white/4">
                {bySpecies.map((s) => (
                  <tr key={s.sp} className="hover:bg-white/[0.02] transition-colors">
                    <td className="px-4 py-2.5 text-white/70 font-medium capitalize">{s.sp}</td>
                    <td className="px-4 py-2.5 text-white/40 tabular-nums">{s.total}</td>
                    <td className="px-4 py-2.5">
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${s.acquitRate > 30 ? 'text-red-400 bg-red-500/10' : 'text-emerald-400 bg-emerald-500/10'}`}>
                        {s.acquitRate}%
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-white/40 tabular-nums">{s.imprisonRate}%</td>
                    <td className="px-4 py-2.5 text-amber-400 tabular-nums">{s.avgFine ? fmtKES(s.avgFine) : '—'}</td>
                    <td className="px-4 py-2.5 text-red-400 tabular-nums">{s.avgPrison ? fmtMonths(s.avgPrison) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {bySpecies.length === 0 && (
              <div className="flex items-center justify-center py-12">
                <p className="text-white/20 text-sm">No species data available</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── By Court tab ── */}
      {tab === 'courts' && (
        <div className="flex flex-col gap-4">
          <div className="rounded-xl border border-white/6 p-5" style={{ background: '#0a2e18' }}>
            <p className="text-white/55 text-xs font-semibold tracking-[0.1em] uppercase mb-4">Court Performance — Conviction vs Acquittal Rate</p>
            {byCourt.length > 0 ? (
              <div style={{ height: 260 }}>
                <Bar
                  options={{
                    ...baseOpts,
                    indexAxis: 'y' as const,
                    scales: {
                      ...baseOpts.scales,
                      x: { ...baseOpts.scales.x, max: 100, ticks: { ...baseOpts.scales.x.ticks, callback: (v: any) => `${v}%` } },
                    },
                    plugins: { ...baseOpts.plugins, legend: { display: true, labels: { color: '#9ca3af', font: { size: 9 }, boxWidth: 8 } } },
                  } as any}
                  data={{
                    labels: byCourt.map((c) => c.court.length > 28 ? c.court.slice(0, 28) + '…' : c.court),
                    datasets: [
                      { label: 'Conviction %', data: byCourt.map((c) => c.convictRate), backgroundColor: 'rgba(52,211,153,0.7)', borderRadius: 3 },
                      { label: 'Acquittal %',  data: byCourt.map((c) => c.acquitRate),  backgroundColor: 'rgba(239,68,68,0.6)',  borderRadius: 3 },
                    ],
                  }}
                />
              </div>
            ) : (
              <p className="text-white/20 text-sm text-center py-8">No court data available</p>
            )}
          </div>

          {/* Court table */}
          <div className="rounded-xl border border-white/6 overflow-hidden" style={{ background: '#0a2e18' }}>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-white/5">
                  {['Court', 'Total Cases', 'Conviction %', 'Acquittal %'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-white/30 font-semibold text-[10px] uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-white/4">
                {byCourt.map((c) => (
                  <tr key={c.court} className="hover:bg-white/[0.02] transition-colors">
                    <td className="px-4 py-2.5 text-white/70 font-medium max-w-[200px] truncate">{c.court}</td>
                    <td className="px-4 py-2.5 text-white/40 tabular-nums">{c.total}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 rounded-full overflow-hidden bg-white/8">
                          <div className="h-full bg-emerald-500/70 rounded-full" style={{ width: `${c.convictRate}%` }} />
                        </div>
                        <span className="text-emerald-400 tabular-nums text-[10px] w-7">{c.convictRate}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${c.acquitRate > 20 ? 'text-red-400 bg-red-500/10' : 'text-white/35 bg-white/5'}`}>
                        {c.acquitRate}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {byCourt.length === 0 && (
              <div className="flex items-center justify-center py-12">
                <p className="text-white/20 text-sm">No court data available</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Trends tab ── */}
      {tab === 'trends' && (
        <div className="rounded-xl border border-white/6 p-5" style={{ background: '#0a2e18' }}>
          <p className="text-white/55 text-xs font-semibold tracking-[0.1em] uppercase mb-3">Sentencing Outcomes Over Time</p>
          <p className="text-white/20 text-[10px] mb-4">How judicial outcomes have shifted year on year</p>
          {yearTrends.labels.length > 1 ? (
            <div style={{ height: 300 }}>
              <Bar
                options={{
                  ...baseOpts,
                  plugins: { ...baseOpts.plugins, legend: { display: true, labels: { color: '#9ca3af', font: { size: 9 }, boxWidth: 8 } } },
                } as any}
                data={{
                  labels: yearTrends.labels,
                  datasets: [
                    { label: 'Imprisoned',  data: yearTrends.imprisoned, backgroundColor: 'rgba(239,68,68,0.7)',   borderRadius: 3, stack: 'a' },
                    { label: 'Fined Only',  data: yearTrends.fined,      backgroundColor: 'rgba(251,191,36,0.7)', borderRadius: 3, stack: 'a' },
                    { label: 'Fine+Prison', data: yearTrends.both,       backgroundColor: 'rgba(167,139,250,0.7)',borderRadius: 3, stack: 'a' },
                    { label: 'Acquitted',   data: yearTrends.acquitted,  backgroundColor: 'rgba(52,211,153,0.7)', borderRadius: 3, stack: 'a' },
                  ],
                }}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-48">
              <p className="text-white/20 text-sm">Not enough year data to show trends</p>
            </div>
          )}
        </div>
      )}

    </div>
  );
}
